import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES, moneyTick, seqColor } from '../lib/charts';
import { useFilters } from '../contexts/FilterContext';
import { money, int, niceDate, addDays, downloadCsv } from '../lib/format';
import { Card, Spinner, ErrorBox, Empty, Delta } from '../components/ui';
import MultiDropdown from '../components/MultiDropdown';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Weekday × hour mini-heatmap for one item ("when it sells"). Hours are raw
// clock order time, not the 4 AM business day — matches /item-hourly.
export function ItemHourlyHeatmap({ cells }) {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(null));
  let max = 0;
  for (const c of cells) {
    grid[c.weekday][c.hour] = c;
    max = Math.max(max, c.quantity);
  }
  return (
    <div className="table-wrap">
      <table className="heatmap">
        <thead>
          <tr>
            <th />
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h}>{h % 3 === 0 ? `${h}` : ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {WEEKDAYS.map((day, w) => (
            <tr key={day}>
              <td className="rowlabel">{day}</td>
              {Array.from({ length: 24 }, (_, h) => {
                const cell = grid[w][h];
                return (
                  <td
                    key={h}
                    style={{ background: seqColor(cell ? cell.quantity : 0, max) }}
                    title={cell
                      ? `${day} ${h}:00 — ${int(cell.quantity)} sold, ${money(cell.revenue)}`
                      : `${day} ${h}:00 — none sold`}
                  >
                    {cell ? int(cell.quantity) : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="legend-note">Darker = more sold · hours are local clock time at order · hover for exact counts.</div>
    </div>
  );
}

// Per-store breakdown for one item ("where it sells"): quantity split by
// channel — online vs rung in at the POS — from /item-locations. Rows sum to
// the item's Qty in the ranking table (same filters server-side).
export function ItemLocationsTable({ rows, locations = [], itemName = 'item' }) {
  const nameOf = (id) => {
    const l = locations.find(x => String(x.ID) === String(id));
    return l ? l.NAME : `Store ${id}`;
  };
  const sorted = rows
    .map(r => ({ ...r, store: nameOf(r.locationId) }))
    .sort((a, b) => b.quantity - a.quantity || a.store.localeCompare(b.store));
  const total = sorted.reduce((t, r) => t + r.quantity, 0);
  const online = sorted.reduce((t, r) => t + r.onlineQty, 0);
  const pct = (n, d) => (d ? `${((n / d) * 100).toFixed(0)}%` : '—');
  const exportCsv = () => downloadCsv(`where-it-sells-${String(itemName).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`, [
    { key: 'store', label: 'Store' },
    { key: 'onlineQty', label: 'Online' },
    { key: 'inStoreQty', label: 'In store' },
    { key: 'quantity', label: 'Total' },
    { key: 'revenue', label: 'Revenue', csv: r => (r.revenue / 100).toFixed(2) },
  ], sorted);
  return (
    <div>
      <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
        <table className="data" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th className="no-sort">Store</th>
              <th className="num no-sort" title="Ordered through the website or app">Online</th>
              <th className="num no-sort" title="Rung in at the store: counter, phone and dine-in">In store</th>
              <th className="num no-sort">Total</th>
              <th className="num no-sort" title="This store's share of the item's total quantity">Share</th>
              <th className="num no-sort" title="Share of this store's quantity that was ordered online">Online %</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.locationId}>
                <td style={{ whiteSpace: 'normal' }}>{r.store}</td>
                <td className="num">{int(r.onlineQty)}</td>
                <td className="num">{int(r.inStoreQty)}</td>
                <td className="num">{int(r.quantity)}</td>
                <td className="num">{pct(r.quantity, total)}</td>
                <td className="num">{pct(r.onlineQty, r.quantity)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 600 }}>
              <td>All {sorted.length} stores</td>
              <td className="num">{int(online)}</td>
              <td className="num">{int(total - online)}</td>
              <td className="num">{int(total)}</td>
              <td className="num" />
              <td className="num">{pct(online, total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span className="legend-note">Online = ordered through the website or app · In store = rung in at the POS</span>
        <button className="btn-ghost" onClick={exportCsv}>⬇ CSV</button>
      </div>
    </div>
  );
}

// Product Mix — modernized "Sales by Main Order Item". Top/bottom sellers with
// expandable per-item size & modifier detail, plus a per-item sales trend.
export default function ProductMixPage() {
  const { reportParams, startDate, endDate, compareMode, locations } = useFilters();

  // Local report controls
  const [topN, setTopN] = useState(10);
  const [showBottom, setShowBottom] = useState(false);
  const [granularity, setGranularity] = useState('week');
  const [metric, setMetric] = useState('qty');
  const [menus, setMenus] = useState([]);
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Report data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modifier detail (lazy-loaded on first expand, one request for all items)
  const [expanded, setExpanded] = useState(new Set());
  const [detail, setDetail] = useState(null);       // { sizeBreakdown, optionBreakdown }
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const seq = useRef(0);
  const firstLoad = useRef(true); // initial fetch skips the filter debounce

  // "When it sells" (lazy-loaded on first expand, one request for all items)
  const [hourly, setHourly] = useState(null);       // rows from /item-hourly
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [hourlyError, setHourlyError] = useState('');
  const [hoursOpen, setHoursOpen] = useState(new Set()); // items with the heatmap shown

  // "Where it sells" (lazy-loaded on first expand, one request for all items)
  const [byStore, setByStore] = useState(null);     // rows from /item-locations
  const [byStoreLoading, setByStoreLoading] = useState(false);
  const [byStoreError, setByStoreError] = useState('');

  useEffect(() => {
    axios.get('/api/reports/menus')
      .then(res => setMenus(res.data || []))
      .catch(() => setMenus([]));
  }, []);

  useEffect(() => {
    if (selectedMenus.length === 0) {
      setCategories([]);
      setSelectedCategories([]);
      return;
    }
    axios.get('/api/reports/categories-by-menu', { params: { menuIds: selectedMenus.join(',') } })
      .then(res => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, [selectedMenus]);

  const queryParams = useMemo(() => ({
    startDate,
    endDate,
    top: topN,
    showBottom,
    selectedMenus: selectedMenus.join(','),
    selectedCategories: selectedCategories.join(','),
    selectedLocations: reportParams.locations || '',
  }), [startDate, endDate, topN, showBottom, selectedMenus, selectedCategories, reportParams.locations]);

  useEffect(() => {
    const mySeq = ++seq.current;
    setLoading(true);
    setError('');
    setExpanded(new Set());
    setDetail(null);
    setDetailError('');
    setHourly(null);
    setHourlyError('');
    setHoursOpen(new Set());
    setByStore(null);
    setByStoreError('');
    // Debounce + abort: a rapid filter-flipper otherwise fires the heavy
    // report query on EVERY click, and each abandoned request still runs to
    // completion. 350ms coalesces a flurry into one request; the abort stops
    // the browser waiting on answers nobody wants (the seq guard still
    // protects against out-of-order arrivals). The FIRST load fires
    // immediately — only changes need coalescing.
    const ctl = new AbortController();
    const run = () => {
      axios.get('/api/reports/sales-by-main-order-items', { params: { ...queryParams, granularity }, signal: ctl.signal })
        .then(res => {
          if (seq.current !== mySeq) return;
          setRows(res.data || []);
          setLoading(false);
        })
        .catch(err => {
          if (seq.current !== mySeq || err.code === 'ERR_CANCELED') return;
          setError(err.response?.data?.error || 'Failed to load product mix');
          setLoading(false);
        });
    };
    if (firstLoad.current) {
      firstLoad.current = false;
      run();
      return () => ctl.abort();
    }
    const timer = setTimeout(run, 350);
    return () => { clearTimeout(timer); ctl.abort(); };
  }, [queryParams, granularity]);

  // Period-over-period: same items, current vs the equal-length prior window,
  // both measured by /items-performance so the delta compares like with like.
  const [popData, setPopData] = useState(null); // { curr: Map, prev: Map }
  const popSeq = useRef(0);

  // Aggregate the per-period rows into one line per item
  const items = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = String(r.menuItemId);
      if (!map.has(key)) map.set(key, { menuItemId: r.menuItemId, itemName: r.itemName, qty: 0, revenue: 0 });
      const it = map.get(key);
      it.qty += Number(r.totalSales) || 0;
      it.revenue += Number(r.totalRevenue) || 0;
    }
    const list = [...map.values()];
    list.sort((a, b) => showBottom ? a.qty - b.qty : b.qty - a.qty);
    return list;
  }, [rows, showBottom]);

  const totalQty = items.reduce((a, r) => a + r.qty, 0);
  const maxQty = items.reduce((a, r) => Math.max(a, r.qty), 0);

  // Fetch current + prior-window performance for the listed items
  useEffect(() => {
    setPopData(null);
    if (items.length === 0) return;
    const mySeq = ++popSeq.current;
    const itemIds = items.map(it => it.menuItemId).join(',');
    const days = Math.round((Date.parse(endDate) - Date.parse(startDate)) / 86400000) + 1;
    // Comparison window: same range last year (364 days, weekday-aligned) or
    // the equal-length window immediately before — mirrors parseWindow().
    const priorRange = compareMode === 'priorYear'
      ? { startDate: addDays(startDate, -364), endDate: addDays(endDate, -364) }
      : { startDate: addDays(startDate, -days), endDate: addDays(startDate, -1) };
    const common = {
      itemIds,
      ...(reportParams.locations ? { locations: reportParams.locations } : {}),
    };
    const ctl = new AbortController();
    Promise.all([
      axios.get('/api/reports/items-performance', { params: { ...common, startDate, endDate }, signal: ctl.signal }),
      axios.get('/api/reports/items-performance', {
        params: { ...common, ...priorRange },
        signal: ctl.signal,
      }),
    ])
      .then(([currRes, prevRes]) => {
        if (popSeq.current !== mySeq) return;
        setPopData({
          curr: new Map(currRes.data.map(r => [String(r.menuItemId), r])),
          prev: new Map(prevRes.data.map(r => [String(r.menuItemId), r])),
        });
      })
      .catch(() => { /* delta column stays blank — table itself is unaffected */ });
    return () => ctl.abort(); // superseded item set — stop waiting on the old one
    // items identity changes with every data load; key on the ids string instead
    // eslint-disable-next-line
  }, [items.map(it => it.menuItemId).join(','), startDate, endDate, reportParams.locations, compareMode]);

  const popFor = (menuItemId) => {
    if (!popData) return null;
    const key = String(menuItemId);
    return {
      curr: popData.curr.get(key) || { quantity: 0, revenue: 0 },
      prev: popData.prev.get(key) || null,
    };
  };

  const fetchDetail = () => {
    if (detail || detailLoading) return;
    setDetailLoading(true);
    setDetailError('');
    axios.get('/api/reports/sales-by-main-order-items-options', { params: queryParams })
      .then(res => {
        setDetail(res.data || { sizeBreakdown: [], optionBreakdown: [] });
        setDetailLoading(false);
      })
      .catch(err => {
        setDetailError(err.response?.data?.error || 'Failed to load modifier detail');
        setDetailLoading(false);
      });
  };

  const fetchHourly = () => {
    if (hourly || hourlyLoading || items.length === 0) return;
    setHourlyLoading(true);
    setHourlyError('');
    axios.get('/api/reports/item-hourly', {
      params: {
        startDate,
        endDate,
        itemIds: items.map(it => it.menuItemId).join(','),
        ...(reportParams.locations ? { locations: reportParams.locations } : {}),
      },
    })
      .then(res => {
        setHourly(res.data || []);
        setHourlyLoading(false);
      })
      .catch(err => {
        setHourlyError(err.response?.data?.error || 'Failed to load selling hours');
        setHourlyLoading(false);
      });
  };

  const fetchByStore = () => {
    if (byStore || byStoreLoading || items.length === 0) return;
    setByStoreLoading(true);
    setByStoreError('');
    axios.get('/api/reports/item-locations', {
      params: {
        startDate,
        endDate,
        itemIds: items.map(it => it.menuItemId).join(','),
        ...(reportParams.locations ? { locations: reportParams.locations } : {}),
      },
    })
      .then(res => {
        setByStore(res.data || []);
        setByStoreLoading(false);
      })
      .catch(err => {
        setByStoreError(err.response?.data?.error || 'Failed to load the store breakdown');
        setByStoreLoading(false);
      });
  };

  // Selling hours are opt-in per item — head office rarely wants them, store
  // operators do — so the heatmap query only runs once somebody asks.
  const toggleHours = (menuItemId) => {
    fetchHourly();
    setHoursOpen(prev => {
      const next = new Set(prev);
      const key = String(menuItemId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleExpand = (menuItemId) => {
    fetchDetail();
    fetchByStore();
    setExpanded(prev => {
      const next = new Set(prev);
      const key = String(menuItemId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Group size + modifier breakdown for one item
  const itemDetail = (menuItemId) => {
    if (!detail) return null;
    const id = String(menuItemId);
    const sizes = detail.sizeBreakdown.filter(s => String(s.menuItemId) === id);
    const options = detail.optionBreakdown.filter(o => String(o.menuItemId) === id);
    if (sizes.length === 0 && options.length === 0) return [];
    const map = new Map();
    for (const s of sizes) {
      map.set(s.sizeName || '', { sizeName: s.sizeName, sizeCount: Number(s.sizeCount) || 0, options: [] });
    }
    for (const o of options) {
      const k = o.sizeName || '';
      if (!map.has(k)) map.set(k, { sizeName: o.sizeName, sizeCount: null, options: [] });
      map.get(k).options.push(o);
    }
    return [...map.values()].sort((a, b) => (b.sizeCount || 0) - (a.sizeCount || 0));
  };

  // Trend chart: one series per item (cap at 8 — fixed palette, never cycled)
  const chart = useMemo(() => {
    const periods = [...new Set(rows.map(r => r.periodEndDate))].sort();
    const charted = items.slice(0, 8);
    return {
      labels: periods.map(p => niceDate(String(p).slice(0, 10))),
      datasets: charted.map((it, idx) => ({
        label: it.itemName,
        data: periods.map(p => {
          const found = rows.find(r => String(r.menuItemId) === String(it.menuItemId) && r.periodEndDate === p);
          if (!found) return 0;
          return metric === 'qty' ? Number(found.totalSales) : Number(found.totalRevenue);
        }),
        borderColor: SERIES[idx],
        backgroundColor: SERIES[idx],
        borderWidth: 2,
        pointRadius: periods.length > 30 ? 0 : 2.5,
        tension: 0.25,
      })),
      truncated: items.length > 8,
    };
  }, [rows, items, metric]);

  const exportCsv = () => downloadCsv('product-mix.csv', [
    { key: 'itemName', label: 'Item' },
    { key: 'qty', label: 'Quantity' },
    { key: 'revenue', label: 'Revenue', csv: r => (r.revenue / 100).toFixed(2) },
  ], items);

  const gran = [['day', 'Daily'], ['week', 'Weekly'], ['month', 'Monthly']];

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Card
        tour="pm-table"
        title={`${showBottom ? 'Bottom' : 'Top'} ${topN} Items`}
        sub={`${niceDate(startDate)} – ${niceDate(endDate)} · by quantity sold · ${compareMode === 'priorYear' ? '“vs LY” compares the same period last year (weekday-aligned)' : '“vs Prior” compares the equal-length window before'} · click “Detail” for stores, online vs in-store, sizes & modifiers`}
        actions={
          <div className="filterbar">
            <div className="chip-row">
              <button className={`chip ${!showBottom ? 'active' : ''}`} onClick={() => setShowBottom(false)}>Top</button>
              <button className={`chip ${showBottom ? 'active' : ''}`} onClick={() => setShowBottom(true)}>Bottom</button>
            </div>
            <div className="chip-row">
              {[10, 20, 50].map(n => (
                <button key={n} className={`chip ${topN === n ? 'active' : ''}`} onClick={() => setTopN(n)}>{n}</button>
              ))}
            </div>
            <MultiDropdown
              icon="📖"
              allLabel="All menus"
              options={menus.map(m => ({ id: m.item_id, label: m.name }))}
              selected={selectedMenus}
              onChange={setSelectedMenus}
            />
            {selectedMenus.length > 0 && (
              <MultiDropdown
                icon="🗂"
                allLabel="All categories"
                options={categories.map(c => ({ id: c.categoryId, label: c.categoryName }))}
                selected={selectedCategories}
                onChange={setSelectedCategories}
              />
            )}
            <button className="btn-ghost" onClick={exportCsv}>⬇ CSV</button>
          </div>
        }
      >
        {loading ? <Spinner label="Ranking the menu…" /> : error ? <ErrorBox message={error} /> : items.length === 0 ? <Empty /> : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="no-sort" style={{ width: 30 }} />
                  <th className="no-sort">Item</th>
                  <th className="num no-sort">Qty</th>
                  <th className="num no-sort">{compareMode === 'priorYear' ? 'Qty vs LY' : 'Qty vs Prior'}</th>
                  <th className="no-sort" style={{ width: '15%' }} />
                  <th className="num no-sort">Revenue</th>
                  <th className="num no-sort">{compareMode === 'priorYear' ? 'Rev vs LY' : 'Rev vs Prior'}</th>
                  <th className="num no-sort">Avg Price</th>
                  <th className="num no-sort">Share</th>
                  <th className="no-sort" style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {items.map((it, rank) => {
                  const isOpen = expanded.has(String(it.menuItemId));
                  const groups = isOpen ? itemDetail(it.menuItemId) : null;
                  const pop = popFor(it.menuItemId);
                  const isNew = pop && pop.curr.quantity > 0 && (!pop.prev || pop.prev.quantity === 0);
                  return (
                    <React.Fragment key={it.menuItemId}>
                      <tr>
                        <td style={{ color: 'var(--ink-muted)' }}>{rank + 1}</td>
                        <td style={{ whiteSpace: 'normal' }}>{it.itemName}</td>
                        <td className="num">{int(it.qty)}</td>
                        <td className="num">
                          {!pop ? <span style={{ color: 'var(--ink-muted)' }}>…</span>
                            : isNew ? <span className="pill" title="No sales in the comparison period">new</span>
                            : <Delta curr={pop.curr.quantity} prev={pop.prev?.quantity} />}
                        </td>
                        <td>
                          <span
                            className="bar"
                            style={{ width: `${maxQty ? Math.max(2, (it.qty / maxQty) * 100) : 0}%` }}
                          />
                        </td>
                        <td className="num">{money(it.revenue)}</td>
                        <td className="num">
                          {!pop ? <span style={{ color: 'var(--ink-muted)' }}>…</span>
                            : isNew ? <span style={{ color: 'var(--ink-muted)' }}>—</span>
                            : <Delta curr={pop.curr.revenue} prev={pop.prev?.revenue} />}
                        </td>
                        <td className="num">{it.qty ? money(it.revenue / it.qty) : '—'}</td>
                        <td className="num">{totalQty ? `${((it.qty / totalQty) * 100).toFixed(1)}%` : '—'}</td>
                        <td>
                          <button className="btn-ghost" data-tour="pm-detail" onClick={() => toggleExpand(it.menuItemId)}>
                            {isOpen ? '▾ Hide' : '▸ Detail'}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={10} style={{ background: '#f7f6f2', whiteSpace: 'normal', padding: '10px 16px' }}>
                            <div data-tour="pm-where" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>Where it sells</div>
                              {byStoreLoading && <Spinner label="Loading stores…" />}
                              {byStoreError && <ErrorBox message={byStoreError} />}
                              {byStore && (() => {
                                const storeRows = byStore.filter(c => String(c.menuItemId) === String(it.menuItemId));
                                return storeRows.length === 0
                                  ? <div className="legend-note">No sales recorded in this window.</div>
                                  : <ItemLocationsTable rows={storeRows} locations={locations} itemName={it.itemName} />;
                              })()}
                            </div>
                            {detailLoading && <Spinner label="Loading sizes & modifiers…" />}
                            {detailError && <ErrorBox message={detailError} />}
                            {groups && groups.length === 0 && <Empty>No size or modifier detail for this item.</Empty>}
                            {groups && groups.length > 0 && (
                              <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                                {groups.map((g, gi) => (
                                  <div key={gi} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                                    <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>
                                      {g.sizeName || 'No size'}
                                      {g.sizeCount != null && (
                                        <span className="pill" style={{ marginLeft: 8 }}>{int(g.sizeCount)} sold</span>
                                      )}
                                    </div>
                                    {g.options.length === 0 ? (
                                      <div className="legend-note">No modifiers recorded</div>
                                    ) : (
                                      <table className="data" style={{ fontSize: 12 }}>
                                        <thead>
                                          <tr>
                                            <th className="no-sort">Modifier</th>
                                            <th className="num no-sort">Count</th>
                                            <th className="num no-sort" title="Of the orders for this size (the “sold” count above), the share that added this modifier">On % of orders</th>
                                            <th className="num no-sort">Revenue</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {g.options
                                            .slice()
                                            .sort((a, b) => Number(b.optionCount) - Number(a.optionCount))
                                            .slice(0, 12)
                                            .map((o, oi) => (
                                              <tr key={oi}>
                                                <td style={{ whiteSpace: 'normal' }}>{o.optionName}</td>
                                                <td className="num">{int(o.optionCount)}</td>
                                                <td className="num">
                                                  {g.sizeCount ? `${((Number(o.optionCount) / g.sizeCount) * 100).toFixed(0)}%` : '—'}
                                                </td>
                                                <td className="num">{money(o.optionRevenue)}</td>
                                              </tr>
                                            ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginTop: 10 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ fontWeight: 600, fontSize: 12.5 }}>When it sells</div>
                                <button className="btn-ghost" onClick={() => toggleHours(it.menuItemId)}>
                                  {hoursOpen.has(String(it.menuItemId)) ? '▾ Hide selling hours' : '▸ Show selling hours'}
                                </button>
                              </div>
                              {hoursOpen.has(String(it.menuItemId)) && (
                                <div style={{ marginTop: 6 }}>
                                  {hourlyLoading && <Spinner label="Loading selling hours…" />}
                                  {hourlyError && <ErrorBox message={hourlyError} />}
                                  {hourly && (() => {
                                    const cells = hourly.filter(c => String(c.menuItemId) === String(it.menuItemId));
                                    return cells.length === 0
                                      ? <div className="legend-note">No orders recorded in this window.</div>
                                      : <ItemHourlyHeatmap cells={cells} />;
                                  })()}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        tour="pm-trend"
        title="Item Sales Over Time"
        sub={chart.truncated ? 'showing the first 8 items — narrow with the menu/category filters for others' : 'per selected item'}
        actions={
          <div className="filterbar">
            <div className="chip-row">
              <button className={`chip ${metric === 'qty' ? 'active' : ''}`} onClick={() => setMetric('qty')}>Quantity</button>
              <button className={`chip ${metric === 'revenue' ? 'active' : ''}`} onClick={() => setMetric('revenue')}>Revenue</button>
            </div>
            <div className="chip-row">
              {gran.map(([id, label]) => (
                <button key={id} className={`chip ${granularity === id ? 'active' : ''}`} onClick={() => setGranularity(id)}>{label}</button>
              ))}
            </div>
          </div>
        }
      >
        <div className="chart-box tall">
          {loading ? <Spinner /> : chart.datasets.length === 0 ? <Empty /> : (
            <Line
              data={{ labels: chart.labels, datasets: chart.datasets }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' },
                  tooltip: {
                    callbacks: {
                      label: ctx => metric === 'qty'
                        ? ` ${ctx.dataset.label}: ${int(ctx.parsed.y)}`
                        : ` ${ctx.dataset.label}: ${money(ctx.parsed.y)}`,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: metric === 'qty' ? {} : { callback: moneyTick },
                  },
                  x: { grid: { display: false }, ticks: { maxTicksLimit: 12 } },
                },
              }}
            />
          )}
        </div>
      </Card>
    </div>
  );
}
